import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";

const PRESETS = [3, 5, 10, 20] as const;

/**
 * Silent meditation timer. Countdown with a soft bell at start and completion.
 * Complements <BreathTimer /> for open sits without prescribed patterns.
 */
export function MeditationTimer({ defaultMinutes = 5 }: { defaultMinutes?: number }) {
  const [minutes, setMinutes] = useState<number>(defaultMinutes);
  const [remaining, setRemaining] = useState<number>(defaultMinutes * 60);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const startRef = useRef<number>(0);
  const baseRef = useRef<number>(defaultMinutes * 60);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const chime = (freq = 528) => {
    if (typeof window === "undefined") return;
    try {
      const AudioCtor: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtor) return;
      audioCtxRef.current ??= new AudioCtor();
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.25, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 2.3);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    if (!running) return;
    startRef.current = performance.now();
    const startRemaining = remaining;
    const tick = () => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      const next = Math.max(0, startRemaining - elapsed);
      setRemaining(next);
      if (next <= 0) {
        setRunning(false);
        setDone(true);
        chime(432);
        setTimeout(() => chime(432), 900);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const start = () => {
    if (done) reset();
    setRunning(true);
    chime(528);
  };

  const reset = () => {
    setRunning(false);
    setDone(false);
    setRemaining(baseRef.current);
  };

  const setPreset = (m: number) => {
    setRunning(false);
    setDone(false);
    setMinutes(m);
    baseRef.current = m * 60;
    setRemaining(m * 60);
  };

  const mm = Math.floor(remaining / 60);
  const ss = Math.floor(remaining % 60);
  const pct = baseRef.current > 0 ? 1 - remaining / baseRef.current : 0;

  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl border hairline bg-charcoal p-6 sm:p-8">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Silent Sit</p>

      <div
        className="relative flex items-center justify-center"
        style={{ width: 180, height: 180 }}
        role="timer"
        aria-live="polite"
      >
        <svg viewBox="0 0 100 100" className="absolute inset-0" aria-hidden>
          <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="2" />
          <circle
            cx="50" cy="50" r="46" fill="none"
            stroke="hsl(var(--gold))" strokeWidth="2"
            strokeDasharray={2 * Math.PI * 46}
            strokeDashoffset={2 * Math.PI * 46 * (1 - pct)}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="text-center">
          <p className="font-serif text-5xl text-gold-luminous tabular-nums">
            {mm}:{ss.toString().padStart(2, "0")}
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {done ? "Complete" : running ? "Sitting" : `${minutes} min`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {PRESETS.map((m) => (
          <button
            key={m}
            onClick={() => setPreset(m)}
            className={`rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] ${
              minutes === m
                ? "border-gold bg-gold text-obsidian"
                : "hairline text-muted-foreground hover:border-gold hover:text-gold"
            }`}
          >
            {m} min
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={running ? () => setRunning(false) : start}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-gold hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-gold"
          aria-label={running ? "Pause meditation" : "Start meditation"}
        >
          {running ? <Pause size={18} /> : <Play size={18} className="translate-x-[1px]" />}
        </button>
        <button
          onClick={reset}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border hairline text-gold hover:bg-gold hover:text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
          aria-label="Reset meditation"
        >
          <RotateCcw size={16} />
        </button>
      </div>
    </div>
  );
}
